import React from 'react';
import type { BookingDetail } from '../../../types';

interface BookingClientInfoProps {
  client: BookingDetail['client'];
}

export const BookingClientInfo: React.FC<BookingClientInfoProps> = ({ client }) => {
  if (!client) {
    return (
      <div className="mx-4 mb-4 p-4 bg-white border border-gray-200 rounded-xl">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
          Client Information
        </h3>
        <div className="text-sm text-gray-500">No client information available</div>
      </div>
    );
  }

  return (
    <div className="mx-4 mb-4 p-4 bg-white border border-gray-200 rounded-xl">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
        Client Information
      </h3>

      <div className="space-y-3">
        {/* Client Name */}
        <div className="text-lg font-semibold text-gray-900">
          {client.name || 'Unknown'}
        </div>

        {/* Phone - Clickable on mobile */}
        {client.phone && (
          <a
            href={`tel:${client.phone}`}
            className="flex items-center gap-2 py-2 px-3 -mx-3 rounded-lg text-blue-600 hover:bg-blue-50 active:bg-blue-100 transition-colors"
            aria-label={`Call ${client.name} at ${client.phone}`}
          >
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            <div className="flex-1 min-w-0">
              <div className="text-base font-medium">
                {client.phone}
              </div>
              <div className="text-xs text-gray-500 md:hidden">
                Tap to call
              </div>
            </div>
          </a>
        )}

        {/* Email */}
        {client.email && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <svg className="w-4 h-4 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span className="break-all">{client.email}</span>
          </div>
        )}

        {/* Client Notes (Private) */}
        {client.private_notes && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="flex items-start gap-2">
              <svg className="w-4 h-4 flex-shrink-0 text-amber-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-amber-700 mb-1">Client Notes</div>
                <p className="text-sm text-gray-700 leading-relaxed">{client.private_notes}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
